/**
 * Agent Schedule Service
 *
 * Frontend API client for the agent scheduling system.
 * Handles CRUD, run management, prompt versioning, and log retrieval
 * for scheduled agent executions.
 */

const V1_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api/v1'
  : 'http://localhost:7777/api/v1';

// --- Types ---

export type ScheduleStatus = 'active' | 'disabled' | 'running';
export type RunStatus = 'success' | 'failure' | 'timeout' | 'skipped' | 'running' | 'pending';
export type TriggerType = 'cron' | 'event' | 'manual' | 'dependency';
export type LogType = 'info' | 'warning' | 'error' | 'context' | 'run_start' | 'run_end';

export interface Schedule {
  id: string;
  name: string;
  description: string | null;
  cronExpression: string | null;
  enabled: boolean;
  promptType: string;
  model: string | null;
  reasoningEffort: string;
  maxTurns: number;
  maxRuntimeSeconds: number;
  executionMode: string;
  episodeId: string | null;
  maxCostUsd: number | null;
  eventTriggers: string[] | null;
  eventDelaySeconds: number;
  eventFilterJson: Record<string, unknown> | null;
  dependsOnScheduleId: string | null;
  lastRunAt: string | null;
  lastRunStatus: RunStatus | null;
  lastRunAgentSessionId: string | null;
  currentRunAgentSessionId: string | null;
  nextRunAt: string | null;
  runCount: number;
  consecutiveFailures: number;
  totalCostUsd: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateScheduleData {
  name: string;
  description?: string;
  cronExpression?: string | null;
  eventTriggers?: string[];
  eventDelaySeconds?: number;
  eventFilter?: Record<string, unknown>;
  prompt: string;
  model?: string;
  reasoningEffort?: string;
  maxTurns?: number;
  maxRuntimeSeconds?: number;
  maxCostUsd?: number | null;
  episodeId?: string | null;
  dependsOnScheduleId?: string | null;
}

export interface UpdateScheduleData {
  name?: string;
  description?: string;
  cronExpression?: string | null;
  eventTriggers?: string[];
  eventDelaySeconds?: number;
  eventFilter?: Record<string, unknown>;
  model?: string;
  reasoningEffort?: string;
  maxTurns?: number;
  maxRuntimeSeconds?: number;
  maxCostUsd?: number | null;
  episodeId?: string | null;
  dependsOnScheduleId?: string | null;
}

export interface LogEntry {
  id: number;
  scheduleId: string;
  agentSessionId: string | null;
  runId: string | null;
  logType: LogType;
  message: string;
  metadataJson: Record<string, unknown> | null;
  createdAt: string;
}

export interface RunEntry {
  id: string;
  scheduleId: string;
  status: RunStatus;
  triggerType: TriggerType;
  triggerEvent: string | null;
  totalTokens: number | null;
  totalCost: number | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  durationSeconds: number | null;
}

export interface PromptVersion {
  id: string;
  promptType: string;
  versionNumber: number;
  createdAt: string;
  createdBy: string;
  isActive: boolean;
  changeSummary: string | null;
  contentHash: string;
  contentSize: number;
  content?: string;
}

export interface EventType {
  name: string;
  description: string;
  fireLocation: string;
  payloadFields: string[];
}

export interface PendingEvent {
  id: string;
  scheduleId: string;
  eventName: string;
  eventPayloadJson: unknown;
  fireAt: string;
  sessionId: string | null;
  status: string;
  cancelledReason: string | null;
  createdAt: string;
}

// --- Mapping helpers ---

/** Parse a date string that may be SQLite format (space-separated, no T/Z) */
function parseDateStr(s: string | null | undefined): string {
  if (!s) return '';
  // SQLite dates look like "2026-03-18 07:45:35" — add T and Z for reliable parsing
  if (s.includes(' ') && !s.includes('T')) {
    return s.replace(' ', 'T') + 'Z';
  }
  return s;
}

/** Map a snake_case API schedule to the camelCase Schedule interface */
function mapSchedule(raw: Record<string, unknown>): Schedule {
  return {
    id: raw.id as string,
    name: raw.name as string,
    description: (raw.description as string) ?? null,
    cronExpression: (raw.cron_expression as string) ?? null,
    enabled: !!raw.enabled,
    promptType: (raw.prompt_type as string) ?? '',
    model: (raw.model as string) ?? null,
    reasoningEffort: (raw.reasoning_effort as string) ?? 'high',
    maxTurns: (raw.max_turns as number) ?? 50,
    maxRuntimeSeconds: (raw.max_runtime_seconds as number) ?? 600,
    executionMode: (raw.execution_mode as string) ?? 'ecs',
    episodeId: (raw.episode_id as string) ?? null,
    maxCostUsd: (raw.max_cost_usd as number) ?? null,
    eventTriggers: (raw.event_triggers as string[]) ?? null,
    eventDelaySeconds: (raw.event_delay_seconds as number) ?? 60,
    eventFilterJson: (raw.event_filter as Record<string, unknown>) ?? null,
    dependsOnScheduleId: (raw.depends_on_schedule_id as string) ?? null,
    lastRunAt: parseDateStr(raw.last_run_at as string),
    lastRunStatus: (raw.last_run_status as RunStatus) ?? null,
    lastRunAgentSessionId: (raw.last_run_agent_session_id as string) ?? null,
    currentRunAgentSessionId: (raw.current_run_agent_session_id as string) ?? null,
    nextRunAt: parseDateStr(raw.next_run_at as string),
    runCount: (raw.run_count as number) ?? 0,
    consecutiveFailures: (raw.consecutive_failures as number) ?? 0,
    totalCostUsd: (raw.total_cost_usd as number) ?? 0,
    createdBy: (raw.created_by as string) ?? '',
    createdAt: parseDateStr(raw.created_at as string),
    updatedAt: parseDateStr(raw.updated_at as string),
  };
}

/** Map a snake_case API log entry to the camelCase LogEntry interface */
function mapLogEntry(raw: Record<string, unknown>): LogEntry {
  return {
    id: raw.id as number,
    scheduleId: (raw.schedule_id as string) ?? '',
    agentSessionId: (raw.agent_session_id as string) ?? null,
    runId: (raw.run_id as string) ?? null,
    logType: (raw.log_type as LogType) ?? 'info',
    message: (raw.message as string) ?? '',
    metadataJson: (raw.metadata_json as Record<string, unknown>) ?? null,
    createdAt: parseDateStr(raw.created_at as string),
  };
}

/** Map a snake_case API run entry to the camelCase RunEntry interface */
function mapRunEntry(raw: Record<string, unknown>): RunEntry {
  return {
    id: raw.id as string,
    scheduleId: (raw.schedule_id as string) ?? '',
    status: (raw.status as RunStatus) ?? 'pending',
    triggerType: (raw.trigger_type as TriggerType) ?? 'manual',
    triggerEvent: (raw.trigger_event as string) ?? null,
    totalTokens: raw.total_input_tokens != null && raw.total_output_tokens != null
      ? (raw.total_input_tokens as number) + (raw.total_output_tokens as number)
      : (raw.total_tokens as number) ?? null,
    totalCost: (raw.total_cost_usd as number) ?? (raw.total_cost as number) ?? null,
    errorMessage: (raw.error_message as string) ?? null,
    createdAt: parseDateStr(raw.created_at as string),
    completedAt: parseDateStr(raw.completed_at as string),
    durationSeconds: (raw.duration_seconds as number) ?? null,
  };
}

/** Map a snake_case API prompt version to the camelCase PromptVersion interface */
function mapPromptVersion(raw: Record<string, unknown>): PromptVersion {
  return {
    id: raw.id as string,
    promptType: (raw.prompt_type as string) ?? '',
    versionNumber: (raw.version_number as number) ?? 0,
    createdAt: parseDateStr(raw.created_at as string),
    createdBy: (raw.created_by as string) ?? '',
    isActive: !!(raw.is_active),
    changeSummary: (raw.change_summary as string) ?? null,
    contentHash: (raw.content_hash as string) ?? '',
    contentSize: (raw.content_size as number) ?? 0,
    content: (raw.content as string) ?? undefined,
  };
}

function parseJsonValue(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw ?? null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/** Map a snake_case API pending event to the camelCase PendingEvent interface */
function mapPendingEvent(raw: Record<string, unknown>): PendingEvent {
  return {
    id: raw.id as string,
    scheduleId: (raw.schedule_id as string) ?? '',
    eventName: (raw.event_name as string) ?? '',
    eventPayloadJson: parseJsonValue(raw.event_payload_json),
    fireAt: parseDateStr(raw.fire_at as string),
    sessionId: (raw.session_id as string) ?? null,
    status: (raw.status as string) ?? 'pending',
    cancelledReason: (raw.cancelled_reason as string) ?? null,
    createdAt: parseDateStr(raw.created_at as string),
  };
}

// --- Service ---

export class AgentScheduleService {
  private static async request<T>(
    endpoint: string,
    options: RequestInit = {},
    token?: string
  ): Promise<T> {
    const url = `${V1_API_BASE_URL}${endpoint}`;

    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData?.error || errorData?.message || errorMessage;
      } catch {
        // response body was not JSON or couldn't be parsed
        try {
          const text = await response.text();
          if (text && text.length < 200) errorMessage = text;
        } catch {
          // ignore
        }
      }
      throw new Error(errorMessage);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as unknown as T;
    }

    return response.json();
  }

  // --- CRUD ---

  static async list(token?: string): Promise<Schedule[]> {
    const data = await this.request<{ schedules: Record<string, unknown>[] }>('/agent/schedules', {}, token);
    return Array.isArray(data?.schedules) ? data.schedules.map(mapSchedule) : [];
  }

  static async get(id: string, token?: string): Promise<Schedule> {
    const raw = await this.request<Record<string, unknown>>(`/agent/schedules/${id}`, {}, token);
    return mapSchedule(raw);
  }

  static async create(data: CreateScheduleData, token?: string): Promise<Schedule> {
    const raw = await this.request<Record<string, unknown>>('/agent/schedules', {
      method: 'POST',
      body: JSON.stringify(data),
    }, token);
    return mapSchedule(raw);
  }

  static async update(id: string, data: UpdateScheduleData, token?: string): Promise<Schedule> {
    const raw = await this.request<Record<string, unknown>>(`/agent/schedules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }, token);
    return mapSchedule(raw);
  }

  static async delete(id: string, token?: string): Promise<void> {
    await this.request<void>(`/agent/schedules/${id}`, {
      method: 'DELETE',
    }, token);
  }

  // --- Enable / Disable ---

  static async enable(id: string, token?: string): Promise<Schedule> {
    const raw = await this.request<Record<string, unknown>>(`/agent/schedules/${id}/enable`, {
      method: 'POST',
    }, token);
    return mapSchedule(raw);
  }

  static async disable(id: string, token?: string): Promise<Schedule> {
    const raw = await this.request<Record<string, unknown>>(`/agent/schedules/${id}/disable`, {
      method: 'POST',
    }, token);
    return mapSchedule(raw);
  }

  // --- Run / Stop ---

  static async runNow(id: string, token?: string): Promise<{ sessionId: string }> {
    return this.request<{ sessionId: string }>(`/agent/schedules/${id}/run-now`, {
      method: 'POST',
    }, token);
  }

  static async stop(id: string, token?: string): Promise<void> {
    await this.request<void>(`/agent/schedules/${id}/stop`, {
      method: 'POST',
    }, token);
  }

  // --- Logs ---

  static async getLogs(id: string, token?: string, limit?: number, logType?: LogType): Promise<LogEntry[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (logType) params.append('type', logType);
    const qs = params.toString();
    const endpoint = `/agent/schedules/${id}/logs${qs ? `?${qs}` : ''}`;
    const data = await this.request<{ logs: Record<string, unknown>[] }>(endpoint, {}, token);
    return Array.isArray(data?.logs) ? data.logs.map(mapLogEntry) : [];
  }

  // --- Runs ---

  static async getRuns(id: string, token?: string, limit?: number): Promise<RunEntry[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    const qs = params.toString();
    const endpoint = `/agent/schedules/${id}/runs${qs ? `?${qs}` : ''}`;
    const data = await this.request<{ runs: Record<string, unknown>[] }>(endpoint, {}, token);
    return Array.isArray(data?.runs) ? data.runs.map(mapRunEntry) : [];
  }

  // --- Prompt Versioning ---

  static async getPrompt(id: string, token?: string): Promise<{ content: string; version: number }> {
    const data = await this.request<{ content: string; version: number }>(`/agent/schedules/${id}/prompt`, {}, token);
    return { content: data?.content ?? '', version: data?.version ?? 0 };
  }

  static async getPromptVersions(id: string, token?: string): Promise<PromptVersion[]> {
    const data = await this.request<{ versions: Record<string, unknown>[] }>(`/agent/schedules/${id}/prompt/versions`, {}, token);
    return Array.isArray(data?.versions) ? data.versions.map(mapPromptVersion) : [];
  }

  static async updatePrompt(id: string, content: string, changeSummary?: string, token?: string): Promise<void> {
    await this.request<void>(`/agent/schedules/${id}/prompt`, {
      method: 'PUT',
      body: JSON.stringify({ content, changeSummary }),
    }, token);
  }

  static async activatePromptVersion(id: string, versionId: string, token?: string): Promise<void> {
    await this.request<void>(`/agent/schedules/${id}/prompt/versions/${versionId}/activate`, {
      method: 'POST',
    }, token);
  }

  // --- Pending events ---

  static async getPendingEvents(id: string, token?: string): Promise<PendingEvent[]> {
    const data = await this.request<{ events: Record<string, unknown>[] }>(`/agent/schedules/${id}/pending-events`, {}, token);
    return Array.isArray(data?.events) ? data.events.map(mapPendingEvent) : [];
  }

  static async cancelPendingEvents(id: string, token?: string): Promise<void> {
    await this.request<void>(`/agent/schedules/${id}/pending-events`, {
      method: 'DELETE',
    }, token);
  }

  static async cancelPendingEvent(id: string, eventId: string, token?: string): Promise<void> {
    await this.request<void>(`/agent/schedules/${id}/pending-events/${eventId}`, {
      method: 'DELETE',
    }, token);
  }

  // --- Events ---

  static async getEventTypes(token?: string): Promise<EventType[]> {
    const data = await this.request<{ events: EventType[] }>('/agent/events', {}, token);
    return Array.isArray(data?.events) ? data.events : [];
  }
}
